import type { ReactNode } from "react";
import React from "react";
import { BotType } from "@rosette/ts/src/business/game/BotType.js";
import { useOptionalUser } from "@/components/env/EnvContext.js";
import type { GameClientControls } from "@rosette/ts/src/business/GameClientControls.js";
import { Button } from "@rosette/web_components/src/ui/Button.js";
import { ClientError } from "@rosette/ts/src/business/game/error/ClientError.js";
import {
    getMemburshipYearlyPriceAsMonthly,
} from "@rosette/ts/src/payments/stripe/products.js";
import { useFormatDate } from "@rosette/web_utils/src/time/useFormatDate.js";
import { GameMode } from "@rosette/ts/src/business/game/GameMode.js";
import { motion } from "framer-motion";
import { fadeInOut } from "@/utils/fadeInOut.js";
import type { KnownLobbySettings } from "@rosette/ts/src/business/game/KnownLobbySettings.js";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";
import Link from "next/link";


interface GameSetupSubmitContentProps {
    settings: KnownLobbySettings;
    clientControls: GameClientControls;
    stripeTestMode: boolean;
}


function GameSetupSubmitContent({
    settings,
    clientControls,
    stripeTestMode,
}: GameSetupSubmitContentProps) {
    const user = useOptionalUser();

    // Format the time when the player can play the panda again.
    const startGameError = clientControls.startGameError;
    const pandaLimitData = (
        startGameError instanceof ClientError
            ? startGameError.getExceededPandaLimitData()
            : null
    );
    const pandaAvailableAgainAt = useFormatDate(pandaLimitData?.available_again_at);

    // 1. Check if the user must register for an account.
    const promptToRegister = (!user && settings.requiresAccount());
    let registerPrompt: ReactNode = "An account is required to play using custom settings.";
    if (promptToRegister) {
        if (settings.getBotType() === BotType.HARD) {
            registerPrompt = (
                <>
                    <span>
                        The hard bot refuses challenges from play-
                    </span>
                    <span className="italic">
                        ur
                    </span>
                    <span>
                        -s without an account (how rude!). Luckily, you can
                        {" "}
                        register for an account for free by clicking the button below.
                    </span>
                </>
            );
        } else if (settings.getBotType() === BotType.PANDA) {
            registerPrompt = (
                <span>
                    An account is required to play our ultimate champion, the Panda!
                    {" "}
                    You can register for an account for free by clicking the button below.
                </span>
            );
        }
        return (
            <motion.div key="register-prompt" {...fadeInOut()}>
                <Panel className="mb-6">
                    <p>
                        {registerPrompt}
                    </p>
                </Panel>
                <Button
                    tone="accent"
                    size="lg"
                    className="w-full"
                    href="/register">

                    Create an account
                </Button>
            </motion.div>
        );
    }

    // 2. Check if the user has to wait to play Panda again.
    if (settings.getMode() === GameMode.COMPUTER
        && settings.getBotType() === BotType.PANDA
        && pandaLimitData) {

        const lobbyID = pandaLimitData.ongoing_lobby_id;
        return (
            <motion.div key="already-played-panda" {...fadeInOut()}>
                <Panel className="mb-6">
                    <p>
                        You have already
                        {" "}
                        {lobbyID ? "started" : "played"}
                        {" "}
                        your daily free game against the Panda.
                        {lobbyID && (
                            <>
                                {" "}
                                <Link href={`/game/${lobbyID}`}>
                                    Click here to resume that game.
                                </Link>
                            </>
                        )}
                        {" "}
                        You can play another free game
                        {" "}
                        {pandaAvailableAgainAt}
                        .
                        {" "}
                        Otherwise, join us as a membur and play unlimited games from just
                        {" "}
                        {getMemburshipYearlyPriceAsMonthly(stripeTestMode)}
                        !
                        {" "}
                        <Link href="/premium">
                            Learn more about becoming a membur here.
                        </Link>
                    </p>
                </Panel>
                <div className="pb-4">
                    <Button
                        tone="accent"
                        size="lg"
                        className="w-full"
                        href="/premium">

                        Join the Memburship!
                    </Button>
                </div>
                <div className="p-3">
                    <Button
                        tone="primary"
                        className="w-full"
                        onClick={() => clientControls.startGame(settings.withBotType(BotType.HARD))}>

                        Play Hard Bot
                    </Button>
                </div>
            </motion.div>
        );
    }

    // 3. Show the normal play button.
    return (
        <motion.div key="play" {...fadeInOut()}>
            <Button
                tone="accent"
                size="lg"
                className="w-full"
                onClick={() => clientControls.startGame(settings)}>

                {(() => {
                    if (clientControls.startingGame)
                        return "...";
                    if (startGameError)
                        return "Error: " + startGameError.message;

                    if (settings.getMode() === GameMode.FRIEND)
                        return "Create Link";
                    return "Play";
                })()}
            </Button>
        </motion.div>
    );
}


interface GameSetupSubmitProps {
    settings: KnownLobbySettings;
    clientControls: GameClientControls;
    stripeTestMode: boolean;
}


export function GameSetupSubmit({
    settings,
    clientControls,
    stripeTestMode,
}: GameSetupSubmitProps) {
    return (
        <div className="mt-6 mb-4 max-w-[42em]">
            <GameSetupSubmitContent
                settings={settings}
                clientControls={clientControls}
                stripeTestMode={stripeTestMode} />
        </div>
    );
}
